import React, { useEffect, useState } from "react";
import Table from "../../EntryFile/datatable";
import { Link } from "react-router-dom";
import alertify from "alertifyjs";
import FeatherIcon from "feather-icons-react";
import Tabletop from "../../EntryFile/tabletop";
import { getContactEnquiries, updateEnquiryStatus, deleteEnquiry  } from "../../core/api/contactEnquiry";
import Swal from "sweetalert2";
import { Button, Form, Modal } from "react-bootstrap";

const ContactEnquiries = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [responseForm, setResponseForm] = useState({
    actionTaken: '',
    responseDetails: ''
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const ACTION_TYPES = [
    'Appointment Booked',
    'Information Provided',
    'Referred to Specialist',
    'Follow-up Scheduled',
    'Other'
  ];
  const columns = [
    {
      title: "Customer Name",
      dataIndex: "name",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Response",
      render: (record) => record.status === 'Answered' && record?.actionTaken ? (
        <div>
          <div><strong>Action:</strong> {record.actionTaken}</div>
          <div><small>{record.responseDetails}</small></div>
        </div>
      ) : '-'
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <select
          value={status}
          onChange={(e) => handleChangeStatus(record, e.target.value)}
          className="form-select"
        >
          <option value="Pending">Pending</option>
          <option value="Answered">Answered</option>
        </select>
      ),
    },
  ];

  const fetchData = async ({ page, pageSize }) => {
    setLoading(true);
    try {
      const result = await getContactEnquiries({ status: filterStatus, page, limit:pageSize, search:search || "" });
      setData(result.data.enquiries);
      setTotalItems(result.data.totalItems);
    } catch (error) {
      alertify.error("Failed to fetch contact enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ page: 1, pageSize: 10 });
  }, [filterStatus]);

  useEffect(() => {
    if(search === null) return;
    fetchData({ page: 1, pageSize: 10 });
  }, [search]);

  const handleChangeStatus = (record, newStatus) => {
    if(record.status === newStatus) return;
    Swal.fire({
      title: "Change Status",
      text: "Are you sure you want to change the status?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
          if(newStatus === 'Answered') {
            setSelectedRecord(record);
            setSelectedEnquiry(record);
            setShowResponseModal(true);
            return;
          }
         await handleSubmitResponse(newStatus,record);
      }
    });
  };

  const handleSubmitResponse = async (newStatus,record) => {
    try {
      await updateEnquiryStatus(record.id, {
        status: newStatus,
        ...responseForm
      });
      const updatedData = data.map((enquiry) =>
        enquiry.id === record.id ? { ...enquiry, status: newStatus } : enquiry
      );
      setData(updatedData);
      fetchData({ page: 1, pageSize: 10 });
      setShowResponseModal(false);
      setResponseForm({
        actionTaken: '',
        responseDetails: ''
      })
      setSelectedRecord(null);
      Swal.fire("Updated!", "Status has been changed.", "success");
    } catch (error) {
      alertify.error("Failed to update status");
    }
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Contact Enquiries</h4>
              <h6>Manage contact enquiries</h6>
            </div>
          </div>

          {/* Filter Navigation Tabs */}
          <div className="row comp-section">
            <div className="col-12 d-flex justify-content-center">
              <div>
                <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded nav-justified mb-3 bg-white" style={{ borderRadius: "24px", padding: '2px 2px' }}>
                  {["All","Pending", "Answered"].map((status) => (
                    <li className="nav-item" key={status}>
                      <a
                        className={`nav-link ${filterStatus === status ? "active" : ""}`}
                        onClick={() => setFilterStatus(status === "All" ? "All" : status)}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Enquiries Table */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <Tabletop
                   OnChange={(e) => setSearch(e.target.value)}
                   value={search}
                 />
              </div>
              <div className="table-responsive">
                <Table
                  isLoading={loading}
                  columns={columns}
                  response={{
                    data: data.map((item, index) => ({ ...item })),
                    totalDataCount: totalItems,
                  }}
                  fetchData={fetchData}
                  NoPagination={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
    show={showResponseModal}
    onHide={() => setShowResponseModal(false)}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>Response Details</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group className="mb-3">
        <Form.Label>Action Taken</Form.Label>
        <Form.Select
          value={responseForm.actionTaken}
          onChange={(e) => setResponseForm(prev => ({
            ...prev,
            actionTaken: e.target.value
          }))}
          required
        >
          <option value="">Select Action</option>
          {ACTION_TYPES.map(action => (
            <option key={action} value={action}>{action}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Response Details</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={responseForm.responseDetails}
          onChange={(e) => setResponseForm(prev => ({
            ...prev,
            responseDetails: e.target.value
          }))}
          placeholder="Enter detailed response..."
          required
        />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer className="d-flex justify-content-end">
      <Button variant="secondary" onClick={() => setShowResponseModal(false)}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={() => handleSubmitResponse('Answered',selectedRecord)}
        disabled={!responseForm.actionTaken || !responseForm.responseDetails}
      >
        Submit Response
      </Button>
    </Modal.Footer>
  </Modal>
    </>
  );
};

export default ContactEnquiries;